/**
 * O que faz: cartão "Organização dos contatos" da aba Agentes e o assistente de quatro passos
 *            (Começo → Entendimento → Exemplos → Ativação) com rascunho local e uma única publicação.
 * Onde usa: SecaoAgentes (aba Agentes da página /ia); também aberto pelo "Ajustar regra da coluna" da revisão.
 * Conexões: useOrganizacaoKanban (RPCs obter/publicar), rascunho.ts (funções puras), EditorRegraColuna,
 *           ExemplosOrganizacaoKanban, Dialog/AlertDialog compartilhados.
 * Tabelas: indiretamente lead_classification_settings, lead_classification_column_rules e etapas_kanban
 *          (somente na confirmação; rascunho e prévia não gravam nada).
 * Data: 21/10/2026.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Sparkles, Workflow } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { COLUNAS_INICIAIS } from './contratos';
import { EditorRegraColuna } from './EditorRegraColuna';
import { ExemplosOrganizacaoKanban } from './ExemplosOrganizacaoKanban';
import {
  aplicarIdsPublicados, bloqueioDoPasso, montarColunas, paraRascunho, podeMoverSozinha,
  type ConfiguracaoOrganizacaoTela, type EstadoAssistente, type EtapaOrganizacao,
} from './rascunho';
import { useOrganizacaoKanban } from './useOrganizacaoKanban';

const PASSOS = ['Começo', 'Entendimento', 'Exemplos', 'Ativação'] as const;
const TEXTO_FREQUENCIA = { diaria: 'Uma vez por dia (às 3h)', quatro_horas: 'A cada 4 horas, se houver novidades' } as const;

interface ConfiguracaoOrganizacaoKanbanProps {
  clinicaId: string;
  etapas: EtapaOrganizacao[];
  // Controlado pelo pai para que a revisão ("Ajustar regra da coluna") também possa abrir o assistente.
  assistenteAberto: boolean;
  onAssistenteAbertoChange: (aberto: boolean) => void;
}

export function ConfiguracaoOrganizacaoKanban({ clinicaId, etapas, assistenteAberto, onAssistenteAbertoChange }: ConfiguracaoOrganizacaoKanbanProps) {
  const { consulta } = useOrganizacaoKanban(clinicaId);
  const configuracao = consulta.data ?? null;
  const hibrido = configuracao?.motor === 'hybrid' && configuracao.regras.length > 0;
  const nomes = useMemo(() => new Map(etapas.map((etapa) => [etapa.id, etapa.nome])), [etapas]);
  const automaticas = configuracao?.regras.filter((regra) => regra.modo === 'automatic').length ?? 0;

  return (
    <Card className="lg-card">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4" aria-hidden="true" />Organização dos contatos</CardTitle>
          <CardDescription>
            {hibrido
              ? 'Cada coluna abaixo tem um significado. A organização sugere para onde cada contato deve ir, e você revisa.'
              : 'Diga o que cada coluna significa e a organização sugere para onde cada contato deve ir. Começa só sugerindo; nada muda sem a sua revisão.'}
          </CardDescription>
        </div>
        <Button className="rounded-full" disabled={consulta.isLoading || consulta.isError} onClick={() => onAssistenteAbertoChange(true)}>
          <Workflow className="mr-2 h-4 w-4" aria-hidden="true" />{hibrido ? 'Ajustar organização' : 'Configurar organização'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {consulta.isLoading && <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Carregando organização…</p>}
        {consulta.isError && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            Não foi possível carregar a organização dos contatos.
            <Button size="sm" variant="outline" onClick={() => void consulta.refetch()}>Tentar novamente</Button>
          </div>
        )}
        {hibrido && configuracao && (
          <div className="space-y-3 animate-in fade-in-0 duration-300 motion-reduce:animate-none">
            <div className="flex flex-wrap gap-2">
              <span className="lg-selo lg-selo--neutro">{TEXTO_FREQUENCIA[configuracao.frequencia]}</span>
              <span className="lg-selo lg-selo--neutro">{automaticas > 0 ? `Move sozinho em ${automaticas} ${automaticas === 1 ? 'coluna' : 'colunas'}` : 'Só sugere mudanças'}</span>
            </div>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {configuracao.regras.map((regra) => (
                <li key={regra.etapaId} className="space-y-0.5 px-3 py-2">
                  <p className="text-sm font-medium text-foreground">{nomes.get(regra.etapaId) ?? regra.nome}</p>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{regra.descricao}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
      {/* Montado só quando aberto: cada abertura começa um rascunho novo a partir do que está publicado. */}
      {assistenteAberto && configuracao && (
        <AssistenteOrganizacao
          clinicaId={clinicaId}
          etapas={etapas}
          configuracao={configuracao}
          onFechar={() => onAssistenteAbertoChange(false)}
        />
      )}
    </Card>
  );
}

interface AssistenteOrganizacaoProps {
  clinicaId: string;
  etapas: EtapaOrganizacao[];
  configuracao: ConfiguracaoOrganizacaoTela;
  onFechar: () => void;
}

function estadoInicial(etapas: EtapaOrganizacao[], configuracao: ConfiguracaoOrganizacaoTela): EstadoAssistente | null {
  // Sem publicação, a clínica escolhe o começo; com publicação, o rascunho parte das regras atuais.
  if (configuracao.regras.length === 0) return null;
  const automaticas = configuracao.regras.filter((regra) => regra.modo === 'automatic').map((regra) => regra.etapaId);
  return {
    origem: 'existente', colunas: montarColunas('existente', etapas, configuracao), frequencia: configuracao.frequencia,
    movimento: automaticas.length > 0 ? 'mover' : 'sugerir', automaticas,
  };
}

function AssistenteOrganizacao({ clinicaId, etapas, configuracao, onFechar }: AssistenteOrganizacaoProps) {
  const { publicar } = useOrganizacaoKanban(clinicaId);
  // O rascunho é fotografado na abertura: refetch em segundo plano não sobrescreve o que a pessoa edita.
  const [inicial] = useState(() => estadoInicial(etapas, configuracao));
  const [estado, setEstado] = useState<EstadoAssistente | null>(inicial);
  const [passo, setPasso] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  const tituloPassoRef = useRef<HTMLHeadingElement>(null);
  const alterado = JSON.stringify(estado) !== JSON.stringify(inicial);

  // Ao trocar de passo, o foco vai para o título: teclado e leitor de tela acompanham a mudança.
  useEffect(() => { tituloPassoRef.current?.focus(); }, [passo]);

  const fechar = () => {
    if (publicar.isPending) return;
    if (alterado) { setConfirmarDescarte(true); return; }
    onFechar();
  };
  const escolherComeco = (origem: 'sugerida' | 'existente') => {
    setErro(null);
    setEstado((atual) => ({
      origem, colunas: montarColunas(origem, etapas, origem === 'existente' ? configuracao : null),
      frequencia: atual?.frequencia ?? configuracao.frequencia, movimento: 'sugerir', automaticas: [],
    }));
  };
  const atualizar = (mudanca: (atual: EstadoAssistente) => EstadoAssistente) => setEstado((atual) => (atual ? mudanca(atual) : atual));
  const bloqueio = estado ? bloqueioDoPasso(passo, estado) : 'Escolha como começar.';
  const permitidas = useMemo(() => (estado?.colunas ?? []).filter((coluna) => podeMoverSozinha(coluna, configuracao)), [estado?.colunas, configuracao]);
  const organizadas = estado?.colunas.filter((coluna) => coluna.organizar) ?? [];
  const novas = organizadas.filter((coluna) => coluna.nova);
  // Origens possíveis: colunas que existem ou que serão criadas nesta publicação.
  const colunasOrigem = (estado?.colunas ?? []).filter((coluna) => !coluna.nova || coluna.organizar).map((coluna) => ({ id: coluna.etapaId, nome: coluna.nome }));

  const salvar = () => {
    if (!estado || bloqueioDoPasso(1, estado) || bloqueioDoPasso(2, estado)) return;
    setErro(null);
    publicar.mutate(paraRascunho(estado, configuracao.regrasVersao), {
      onSuccess: ({ novasColunas }) => {
        atualizar((atual) => ({ ...atual, colunas: aplicarIdsPublicados(atual.colunas, novasColunas) }));
        onFechar();
      },
      // Falha (conflito, permissão, rede): o rascunho continua na tela; nada é reenviado sozinho.
      onError: (falha) => setErro(falha instanceof Error && falha.message ? falha.message : 'Verifique sua conexão e tente de novo.'),
    });
  };

  return (
    <>
      <Dialog open onOpenChange={(aberto) => { if (!aberto) fechar(); }}>
        <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 space-y-2 border-b border-border px-4 py-3 pr-12 text-left sm:px-5">
            <DialogTitle className="text-base">Organização dos contatos</DialogTitle>
            <DialogDescription className="text-xs">Nada é salvo até você confirmar no último passo.</DialogDescription>
            <ol className="flex flex-wrap gap-x-3 gap-y-1 text-xs" aria-label="Passos da configuração">
              {PASSOS.map((nome, indice) => (
                <li key={nome} aria-current={passo === indice + 1 ? 'step' : undefined}
                  className={cn('transition-colors duration-200', passo === indice + 1 ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                  {indice + 1}. {nome}
                </li>
              ))}
            </ol>
          </DialogHeader>

          {/* Dono do scroll recebe o scroll-fade (CSS puro, sem estado React nem listener). */}
          <div className="scroll-fade min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
            <div key={passo} className="space-y-4 animate-in fade-in-0 slide-in-from-right-2 duration-200 motion-reduce:animate-none">
              <h3 ref={tituloPassoRef} tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                {passo === 1 && 'Como você quer começar?'}
                {passo === 2 && 'Confira o que cada coluna significa'}
                {passo === 3 && 'Veja se os exemplos vão para o lugar certo'}
                {passo === 4 && 'Como a organização deve agir'}
              </h3>

              {passo === 1 && (
                <div className="space-y-4">
                  <RadioGroup value={estado?.origem ?? ''} onValueChange={(valor) => escolherComeco(valor as 'sugerida' | 'existente')} aria-label="Como começar" className="gap-3">
                    <label htmlFor="organizacao-sugerida" className={cn('flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors duration-200', estado?.origem === 'sugerida' ? 'border-primary bg-accent' : 'border-border')}>
                      <RadioGroupItem value="sugerida" id="organizacao-sugerida" className="mt-0.5" />
                      <span className="space-y-1">
                        <span className="block text-sm font-medium text-foreground">Usar organização sugerida</span>
                        <span className="block text-xs text-muted-foreground">{COLUNAS_INICIAIS.map((coluna) => coluna.nome).join(', ')}. Colunas que ainda não existem só são criadas quando você salvar.</span>
                      </span>
                    </label>
                    <label htmlFor="organizacao-existente" className={cn('flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors duration-200', estado?.origem === 'existente' ? 'border-primary bg-accent' : 'border-border')}>
                      <RadioGroupItem value="existente" id="organizacao-existente" className="mt-0.5" />
                      <span className="space-y-1">
                        <span className="block text-sm font-medium text-foreground">Usar minhas colunas</span>
                        <span className="block text-xs text-muted-foreground">Usa as colunas que já existem no seu Pipeline. Nenhuma é renomeada, removida ou reordenada.</span>
                      </span>
                    </label>
                  </RadioGroup>
                  {estado && (
                    <fieldset className="space-y-2 animate-in fade-in-0 duration-200 motion-reduce:animate-none">
                      <legend className="text-xs font-medium text-muted-foreground">Colunas que a organização vai usar</legend>
                      {estado.colunas.map((coluna) => {
                        const id = `organizar-${coluna.etapaId}`;
                        const protegida = etapas.find((etapa) => etapa.id === coluna.etapaId)?.protegida;
                        return (
                          <div key={coluna.etapaId} className="flex items-start gap-2 rounded-lg border border-border px-3 py-2">
                            <Checkbox id={id} checked={coluna.organizar} className="mt-0.5"
                              onCheckedChange={(marcada) => atualizar((atual) => ({ ...atual, colunas: atual.colunas.map((item) => (item.etapaId === coluna.etapaId ? { ...item, organizar: marcada === true } : item)) }))} />
                            <Label htmlFor={id} className="flex flex-1 flex-wrap items-center gap-2 font-normal">
                              <span className="break-words">{coluna.nome}</span>
                              {coluna.nova && <span className="lg-selo lg-selo--info">Nova</span>}
                              {protegida && <span className="text-[11px] text-muted-foreground">venda, retorno ou evento de anúncio</span>}
                            </Label>
                          </div>
                        );
                      })}
                    </fieldset>
                  )}
                </div>
              )}

              {passo === 2 && estado && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Clique em “Está certo” quando a frase descrever a coluna. Se não, use “Quero ajustar”.</p>
                  {organizadas.map((coluna) => (
                    <EditorRegraColuna
                      key={coluna.etapaId}
                      regra={coluna.regra}
                      colunasOrigem={colunasOrigem}
                      onAlterar={(regra) => atualizar((atual) => ({ ...atual, colunas: atual.colunas.map((item) => (item.etapaId === coluna.etapaId ? { ...item, regra } : item)) }))}
                      onNaoOrganizar={() => atualizar((atual) => ({ ...atual, colunas: atual.colunas.map((item) => (item.etapaId === coluna.etapaId ? { ...item, organizar: false } : item)) }))}
                    />
                  ))}
                </div>
              )}

              {passo === 3 && estado && (
                <ExemplosOrganizacaoKanban colunas={estado.colunas} onAlterar={(colunas) => atualizar((atual) => ({ ...atual, colunas }))} />
              )}

              {passo === 4 && estado && (
                <div className="space-y-5">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">O que fazer com as mudanças</p>
                    <RadioGroup value={estado.movimento} onValueChange={(valor) => atualizar((atual) => ({ ...atual, movimento: valor as 'sugerir' | 'mover' }))} aria-label="O que fazer com as mudanças" className="gap-2">
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="sugerir" id="organizacao-sugerir" />
                        <Label htmlFor="organizacao-sugerir" className="font-normal">Sugerir mudanças para a equipe revisar</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="mover" id="organizacao-mover" disabled={permitidas.length === 0} />
                        <Label htmlFor="organizacao-mover" className={cn('font-normal', permitidas.length === 0 && 'text-muted-foreground')}>Mover nas colunas permitidas</Label>
                      </div>
                    </RadioGroup>
                    {permitidas.length === 0 && (
                      <p className="text-xs text-muted-foreground">Comece revisando as sugestões. Depois você poderá permitir movimentos automáticos nas colunas aprovadas.</p>
                    )}
                    {estado.movimento === 'mover' && permitidas.length > 0 && (
                      <fieldset className="space-y-2 rounded-xl border border-border p-3 animate-in fade-in-0 duration-200 motion-reduce:animate-none">
                        <legend className="px-1 text-xs text-muted-foreground">Colunas em que pode mover sozinha</legend>
                        {permitidas.map((coluna) => (
                          <div key={coluna.etapaId} className="flex items-center gap-2">
                            <Checkbox id={`automatica-${coluna.etapaId}`} checked={estado.automaticas.includes(coluna.etapaId)}
                              onCheckedChange={(marcada) => atualizar((atual) => ({ ...atual, automaticas: marcada === true ? [...atual.automaticas, coluna.etapaId] : atual.automaticas.filter((id) => id !== coluna.etapaId) }))} />
                            <Label htmlFor={`automatica-${coluna.etapaId}`} className="font-normal">{coluna.nome}</Label>
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground">
                          Só move com alta segurança e vindo das colunas permitidas. Colunas com evento de anúncio continuam dependendo
                          da autorização de colunas Meta. Retornos são sempre só sugestão.
                        </p>
                      </fieldset>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Quando analisar</p>
                    <RadioGroup value={estado.frequencia} onValueChange={(valor) => atualizar((atual) => ({ ...atual, frequencia: valor as EstadoAssistente['frequencia'] }))} aria-label="Quando analisar" className="gap-2">
                      {(Object.keys(TEXTO_FREQUENCIA) as Array<keyof typeof TEXTO_FREQUENCIA>).map((frequencia) => (
                        <div key={frequencia} className="flex items-center gap-2">
                          <RadioGroupItem value={frequencia} id={`organizacao-${frequencia}`} />
                          <Label htmlFor={`organizacao-${frequencia}`} className="font-normal">{TEXTO_FREQUENCIA[frequencia]}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground">Só entram contatos que tiveram novidade desde a última análise.</p>
                  </div>
                  <p className="rounded-xl bg-muted p-3 text-sm text-foreground">
                    {organizadas.length} {organizadas.length === 1 ? 'coluna organizada' : 'colunas organizadas'}.
                    {novas.length > 0 && ` Serão criadas: ${novas.map((coluna) => coluna.nome).join(', ')}.`}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 space-y-2 border-t border-border px-4 py-3 sm:px-5">
            {erro && (
              <div role="alert" className="rounded-lg border border-destructive/40 p-2 text-sm animate-in fade-in-0 duration-200 motion-reduce:animate-none">
                <p className="font-medium lg-texto-perigo">Não foi possível salvar. Seus ajustes continuam aqui.</p>
                <p className="text-xs text-muted-foreground">{erro}</p>
              </div>
            )}
            {bloqueio && passo < 4 && <p className="text-xs text-muted-foreground">{bloqueio}</p>}
            <div className="flex flex-wrap justify-between gap-2">
              <Button type="button" variant="outline" className="rounded-full" disabled={publicar.isPending}
                onClick={() => (passo === 1 ? fechar() : setPasso((atual) => atual - 1))}>
                {passo === 1 ? 'Cancelar' : 'Voltar'}
              </Button>
              {passo < 4 ? (
                <Button type="button" className="rounded-full" disabled={Boolean(bloqueio)} onClick={() => setPasso((atual) => atual + 1)}>Continuar</Button>
              ) : (
                <Button type="button" className="rounded-full" disabled={publicar.isPending || !estado} onClick={salvar}>
                  {publicar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  {publicar.isPending ? 'Salvando…' : 'Salvar organização'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmarDescarte} onOpenChange={setConfirmarDescarte}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar ajustes não salvos?</AlertDialogTitle>
            <AlertDialogDescription>As mudanças desta configuração serão perdidas. Nada foi alterado no seu Pipeline.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={onFechar}>Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
